import { formatDayJS, getModifedTime, ModelCommon, UserAttributeDefs, ValidModelType } from '@iotv/datamodel-core'
import { PathDirection } from '@iotv/iotv-v3-types'
import { CycleItem } from './cycle'
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
dayjs.extend(duration)
dayjs.extend(customParseFormat)

const typeName = 'IrrigationSequentialSchedule'

export type ValveProgrammeStructure = {
  dayMask: number[]
  cycles: CycleItem[]
}

export type IrrigationSequentialScheduleProps = {
  name?: string
  createdAt?: number
  createdBy?: string
  updated_at?: number
  active: boolean
  startTime?: string
  areaCovered?: number
  waterApplied?: number
  irrigationCycle?: number
  dayMask?: number[]
  cycles?: CycleItem[]
  zones?: string[]
  valves?: string[]
  mvcDelay?: number
}

const MINUTES_IN_DAY = 24 * 60 //1440

const getTimeDifferenceInMinutes = (time1: Dayjs, time2: Dayjs) => {
  let minuteDifference = time2.diff(time1, 'minute')
  if (minuteDifference < 0) {
    minuteDifference += MINUTES_IN_DAY
  }

  return minuteDifference
}

export class IrrigationSequentialSchedule extends ModelCommon implements IrrigationSequentialScheduleProps {
  constructor(ob: any) {
    super(ob.type || typeName, ob)
    this.active = ob.active
    this.cycles = ob.cycles
    this.dayMask = ob.dayMask
    this.valves = ob.valves ?? []
    this.mvcDelay = ob.mvcDelay ?? 0
  }

  createdAt?: number
  createdBy?: string
  updated_at?: number
  startTime?: string
  areaCovered?: number
  active: boolean
  waterApplied?: number
  irrigationCycle?: number
  dayMask?: number[]
  cycles?: CycleItem[]
  zones?: string[]
  valves?: string[]
  mvcDelay?: number

  getMVCCycle(showAP: boolean = true): ValveProgrammeStructure | null {
    let cycles: CycleItem[] = []
    let zonesUsed = (this.zones ?? []).length
    let delay = this.mvcDelay ?? 0

    try {
      if (this.cycles && zonesUsed !== 0) {
        cycles = this.cycles.map((val) => {
          // set start durations

          const last = val.start_time.length - 1

          let mvcStartTime = getModifedTime(val.start_time[0], delay, 'add')
          let programmeFinishTime = getModifedTime(val.start_time[last], val.duration[last], 'add')

          let duration = 0
          if (zonesUsed === 1) {
            duration = val.duration[0] - delay
          } else {
            let diff = getTimeDifferenceInMinutes(mvcStartTime, programmeFinishTime)
            duration = diff
          }
          duration -= delay // not x2 as the `firstTime` has already been shifted
          duration = Math.min(MINUTES_IN_DAY, duration) // in case the duration is for the entire day
          return {
            start_time: [formatDayJS(mvcStartTime, showAP)],
            duration: [duration],
          }
        })
      }

      let res: ValveProgrammeStructure = {
        dayMask: this.dayMask ?? [],
        cycles: cycles,
      }

      return res
    } catch (e: any) {
      console.log('Missing data in MVCCycle')
      return null
    }
  }

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      Name: {
        type: 'RelatedType',
        editable: false,
        label: 'Name',
        name: 'Name',
        description: 'Name of schedule eg Westerly',
        objectTypeId: 'User' as ValidModelType,
        direction: PathDirection.child,
        edgeTypeId: 'Irrigation_Sequential_Programme_has_Name_User',
      },
      timeCreated: {
        type: 'Number',
        editable: true,
        label: 'Time Created',
        name: 'timeCreated',
        description: 'Date and time schedule was created',
      },
      startTime: {
        type: 'String',
        editable: true,
        label: 'Start Time',
        name: 'startTime',
        description: 'When the first programme starts',
      },
      userCreated: {
        type: 'String',
        editable: true,
        label: 'User Created ',
        name: 'userCreated',
        description: 'User that created schedule',
      },
      areaCovered: {
        type: 'Number',
        editable: true,
        label: 'Area covered',
        name: 'areaCovered',
        description: 'Aggregate of area covered by irrigation ',
      },
      waterApplied: {
        type: 'Number',
        editable: true,
        label: 'Water applied',
        name: 'waterApplied',
        description: 'Calculated amount of water to be applied at x pressure for y duration',
      },
      irrigationCycle: {
        type: 'Number',
        editable: true,
        label: 'Irrigation Cycle Amount',
        name: 'irrigationCycle',
        description: 'The number of programme cycles',
      },
      dayMask: {
        type: 'Enum',
        editable: true,
        label: 'Day Mask Selection',
        name: 'dayMask',
        description: 'When the programme should run (local)',
      },
    }
  }
}

export const forTestingOnly = { getTimeDifferenceInMinutes }
